import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";
import { useParams } from "react-router-dom";

// Utility: default last 30 days range
function getDefaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);

  return {
    fromDate: start.toISOString().split("T")[0],
    toDate: end.toISOString().split("T")[0],
  };
}

export default function useTotalRevenueByStation(range = null) {
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { send, isConnected, registerHandler } = useWebSocketContext();
  const mountedRef = useRef(true);
  const { stationId } = useParams();

  const fetchTotalRevenue = useCallback(async () => {
    if (!mountedRef.current) return;
    if (!isConnected) {
      setError("WebSocket not connected");
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const { fromDate, toDate } = range || getDefaultRange();

      const payload = {
        station_id: stationId?.toString(),
        start_date: fromDate,
        end_date: toDate,
      };

      console.log("Payload being sent (station revenue):", payload);

      const response = await send("cms_get_total_revenue_by_station_id", payload);

      if (!response || response.type !== "cms_get_total_revenue_by_station_id_response") {
        throw new Error("Invalid response from server");
      }

      if (typeof response.data !== "number") {
        throw new Error("Server did not return a valid revenue value");
      }

      console.log("Total revenue for station:", response.data);
      setTotalRevenue(response.data);
    } catch (err) {
      console.error("Error fetching revenue (station):", err);
      setError(err.message);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [isConnected, send, stationId, range]);

  // Live updates subscription
  useEffect(() => {
    const unregister = registerHandler("revenue_update_station", (msg) => {
      if (typeof msg.data === "number" && mountedRef.current) {
        setTotalRevenue(msg.data);
      }
    });
    return () => unregister();
  }, [registerHandler]);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    fetchTotalRevenue();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchTotalRevenue]);

  return { 
    totalRevenue, 
    isLoading, 
    error, 
    refetch: fetchTotalRevenue 
  };
}
